import React, { useEffect, useState } from "react";
import { toastr } from "react-redux-toastr";
import { useMutation } from "../_api/hooks";
import { useSelector, shallowEqual } from "react-redux";
import { gql } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "../_api/hooks";

import { getUserInfoQuery } from "../containers/profile/profile.query";

import { ID, USER } from "../_constant/wordings";

import { isDeviceRegisterInfoEnabled } from "../redux/reducers/appReducer";
import { isNativeApp, nativePlatformName, registerForPush } from "../native/nativeApp";

declare global {
  interface Window {
    getPushNotification: any;
    getPushToken: any;
    pushConfig: any;
  }
}

const patchUserDevice = gql`
  mutation patchUserDevice($UserId: ID, $UserDevice: [UserDeviceInputType], $TimeZoneId: String) {
    UserDevicePatch(UserId: $UserId, UserDevice: $UserDevice, TimeZoneId: $TimeZoneId) {
      PushToken
      MobilePlatform
    }
  }
`;

const InitNotificationFunctions = () => {
  const [patch, { data }] = useMutation(patchUserDevice);
  const [pushConfig, setPushConfig] = useState<any>({
    token: "",
    platform: "",
  });
  const { data: userData } = useQuery(getUserInfoQuery, {
    fetchPolicy: "cache-only",
  });

  const isInfoEnabled = useSelector(isDeviceRegisterInfoEnabled, shallowEqual);

  useEffect(() => {
    if (window.pushConfig.token || window.pushConfig.platform) {
      setPushConfig({ ...window.pushConfig });
    }
  }, []);

  useEffect(() => {
    window.pushConfig = new Proxy({ ...pushConfig } as any, {
      set: (target, prop, value) => {
        target[prop] = value;
        setPushConfig((prevState: any) => {
          return {
            ...prevState,
            [prop]: value,
          };
        });
        return true;
      },
    });
  }, []);

  // The iOS and Android apps fill in pushConfig themselves, as the old native apps did from their web view.
  // Wait until the person has signed in, so the notification permission prompt doesn't appear over sign-in.
  const { isAuthenticated } = useAuth0();
  useEffect(() => {
    if (!isNativeApp || !isAuthenticated || import.meta.env.REACT_APP_NATIVE_PUSH !== "true") {
      return;
    }
    registerForPush(
      (token) => {
        window.pushConfig.platform = nativePlatformName;
        window.pushConfig.token = token;
      },
      (path) => {
        // This component sits outside the router, so move through history and let the router catch up.
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
    ).catch((err) => console.log(err));
  }, [isAuthenticated]);

  useEffect(() => {
    const isAlreadyPatched = localStorage.getItem("isUserDevicePatched");
    const pushConfigFromLocalStorage = isAlreadyPatched ? JSON.parse(isAlreadyPatched) : null;
    const token = pushConfigFromLocalStorage?.token ?? null;

    if (
      pushConfig.token &&
      pushConfig.platform &&
      userData &&
      userData[USER] &&
      userData[USER][ID] &&
      token !== pushConfig.token
    ) {
      const { token, platform } = pushConfig;
      let normalizedPlatform;

      if (platform === "Android") {
        normalizedPlatform = "ANDROID";
      } else {
        normalizedPlatform = "I_OS";
      }
      patch({
        variables: {
          UserId: userData[USER][ID],
          UserDevice: [
            {
              PushToken: token,
              MobilePlatform: normalizedPlatform,
            },
          ],
          TimeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
        .then((res) => {
          if (isInfoEnabled) toastr.success(JSON.stringify(res));
          const pushConfigStringify = JSON.stringify(pushConfig);
          localStorage.setItem("isUserDevicePatched", pushConfigStringify);
        })
        .catch((err) => {
          if (isInfoEnabled) toastr.error(err.toString());
          console.log(err);
        });
    }
  }, [userData, pushConfig]);

  return null;
};

export default InitNotificationFunctions;
